import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getEvents,
  getEventById,
  getAdminEvents,
  getAdminEventById,
  createEvent,
  updateEvent,
  deleteEvent,
} from '../services/events.service';

/**
 * Hook to fetch published events for the public calendar
 * @param {Object} params - Query parameters
 * @returns {Object} Query result
 */
export const useEvents = (params = {}) => {
  return useQuery({
    queryKey: ['events', params],
    queryFn: () => getEvents(params),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook to fetch a single published event
 * @param {number} id - Event ID
 * @returns {Object} Query result
 */
export const useEvent = (id) => {
  return useQuery({
    queryKey: ['events', id],
    queryFn: () => getEventById(id),
    enabled: !!id,
  });
};

/**
 * Hook to fetch all events including drafts (admin)
 * @param {Object} params - Query parameters
 * @returns {Object} Query result
 */
export const useAdminEvents = (params = {}) => {
  return useQuery({
    queryKey: ['events', 'admin', params],
    queryFn: () => getAdminEvents(params),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook to fetch a single event including drafts (admin)
 * @param {number} id - Event ID
 * @returns {Object} Query result
 */
export const useAdminEvent = (id) => {
  return useQuery({
    queryKey: ['events', 'admin', id],
    queryFn: () => getAdminEventById(id),
    enabled: !!id,
  });
};

/**
 * Hook to create event
 * @returns {Object} Mutation result
 */
export const useCreateEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createEvent,
    onSuccess: () => {
      // Invalidate both the public calendar and the admin list
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
};

/**
 * Hook to update event
 * @returns {Object} Mutation result
 */
export const useUpdateEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => updateEvent(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
};

/**
 * Hook to delete event
 * @returns {Object} Mutation result
 */
export const useDeleteEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteEvent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
};
